import React, {
  useCallback,
  useMemo,
  lazy,
  Suspense,
  useState,
  useEffect,
} from 'react';
import { Box, Grid, GridProps } from '@mui/material';
import BoardToolBar from './BoardToolBar';
import {
  useBoardNodesByBoardID,
  useBoardsByID,
  useDuplicateBoardNode,
  useUpdateBoardNode,
} from 'hooks/Board';
import BoardLoadingScreen from './BoardLoadingScreen';
import './style.css';
import SparkBlankBoard from './SparkBlankBoard';
import { useBoardNodesContext } from 'hooks/common';
import {
  transformAllSparkBoardNodesToRectFlowNodes,
  transformSparkBoardNodeToRectFlowNodes,
  useReactFlowHelpers,
} from './helper/useReactFlowHelpers';
import BoardChatContainer from '../Chats/BoardChatContainer';
import { BOARD_ID_SELECTORS } from './helper/constants';
import { Node } from 'reactflow';
import { BoardNodes } from 'types/BoardNode';

// Lazy load view components for code splitting
const BoardListViewContainer = lazy(() => import('./BoardListViewContainer'));
const BoardCanvasViewContainer = lazy(
  () => import('./BoardCanvasViewContainer')
);

type BoardContainerProps = {
  boardID: string | null | undefined;
  onClose?: () => void;
  loadingComponent?: React.ReactNode;
  floatingToolBar?: boolean;
  headerStyles?: GridProps['sx'];
  boardContainerStyles?: GridProps['sx'];
};

type BoardConatinerType = {
  view: 'list' | 'canvas';
  boardID: string | null | undefined;
};

function BoardContainer({
  boardID,
  onClose,
  loadingComponent = null,
  floatingToolBar = true,
  headerStyles = {},
  boardContainerStyles = {},
}: BoardContainerProps) {
  const boardRef = React.useRef<HTMLDivElement>(null);
  const [view, setView] = React.useState<BoardConatinerType>(() => {
    const stored = localStorage.getItem('boardView');
    const currentBoard = stored && JSON.parse(stored);
    return {
      view:
        currentBoard?.view === 'canvas' && currentBoard?.boardID === boardID
          ? 'canvas'
          : 'list',
      boardID: boardID,
    };
  });
  const [isAddingNewNode, setIsAddingNewNode] = React.useState<{
    boardNodeID: string | null;
    isLoading: boolean;
  }>({ boardNodeID: null, isLoading: false });
  const [loadingDelay, setLoadingDelay] = useState(true); // State to control the delay
  const { isLoading: isLoadingBoard, boardChat } = useBoardsByID(boardID);
  const { mutateAsync: duplicateBoardNode } = useDuplicateBoardNode(boardID);
  const [copiedNodes, setCopiedNodes] = React.useState<Node[]>([]);

  const { isLoading: isLoadingBoardNodes, boardNodes } =
    useBoardNodesByBoardID(boardID);

  const { setAllBoardNodes, nodes, setNodes, addToReactFlowNodes } =
    useReactFlowHelpers();

  const { selectedNodes, clearSelection, setSelectedNodes } =
    useBoardNodesContext();
  const { mutateAsync: deleteBoardNode } = useUpdateBoardNode(
    undefined,
    boardID || ''
  );

  // when the boardLoaded, set the nodes
  React.useEffect(() => {
    if (
      !nodes.length &&
      boardID &&
      boardNodes?.length &&
      !isLoadingBoard &&
      !isLoadingBoardNodes
    ) {
      setAllBoardNodes(transformAllSparkBoardNodesToRectFlowNodes(boardNodes));
    }
  }, [
    boardID,
    boardNodes,
    isLoadingBoard,
    isLoadingBoardNodes,
    nodes,
    setAllBoardNodes,
  ]);

  // Memoize handlers to prevent unnecessary rerenders
  const handleViewToggle = useCallback(
    (viewType: 'list' | 'canvas') => {
      setView({ view: viewType, boardID: boardID });
      localStorage.setItem(
        'boardView',
        JSON.stringify({ view: viewType, boardID: boardID })
      );
    },
    [boardID]
  );

  const handleDuplicateNode = React.useCallback(
    (node: BoardNodes) => {
      addToReactFlowNodes(transformSparkBoardNodeToRectFlowNodes(node));
    },
    [addToReactFlowNodes]
  );

  // Batch delete operations for better performance
  const handleKeyEvents = useCallback<
    React.KeyboardEventHandler<HTMLDivElement>
  >(
    async (event) => {
      if (event.key === 'Escape') {
        clearSelection();
      }
      if (
        (event.key === 'Delete' || event.key === 'Backspace') &&
        selectedNodes?.length
      ) {
        // Create batch operation
        const deletePromises = selectedNodes.map((nodeID) => {
          const newNodes = nodes.filter(
            (node) => !selectedNodes.includes(node.id)
          );
          setNodes(newNodes);
          return deleteBoardNode({ isDeleted: true, id: nodeID });
        });
        clearSelection();
        // Execute all deletes in parallel
        await Promise.all(deletePromises);
      }
      // command or control + a to select all nodes
      if (
        (event.key === 'a' || event.key === 'A') &&
        (event.ctrlKey || event.metaKey)
      ) {
        event.preventDefault();
        const allNodeIds = nodes.map((node) => node.id);
        setSelectedNodes(allNodeIds);
      }
      if (
        (event.key === 'c' || event.key === 'C') &&
        event.metaKey &&
        selectedNodes?.length
      ) {
        event.preventDefault();
        const nodesToCopy = nodes.filter((node) =>
          selectedNodes.includes(node.id)
        );

        setCopiedNodes(nodesToCopy);
      }

      if (
        (event.key === 'v' || event.key === 'V') &&
        event.metaKey &&
        selectedNodes?.length
      ) {
        const duplicationPromises = copiedNodes.map(async (node) => {
          const boardNode = node.data.boardNode;
          const response = await duplicateBoardNode({
            boardNodeID: boardNode.id,
          });
          return response.data;
        });

        const duplicatedNodes = await Promise.all(duplicationPromises);

        for (const duplicatedNode of duplicatedNodes) {
          handleDuplicateNode(duplicatedNode);
        }
      }
    },
    [
      selectedNodes,
      clearSelection,
      nodes,
      setNodes,
      deleteBoardNode,
      setSelectedNodes,
      copiedNodes,
      duplicateBoardNode,
      handleDuplicateNode,
    ]
  );

  // Memoize computed values
  const isCanvasView = useMemo(() => view.view === 'canvas', [view]);

  // Delay BoardLoadingScreen visibility after loading states
  useEffect(() => {
    if (!isLoadingBoard && !isLoadingBoardNodes) {
      setTimeout(() => {
        setLoadingDelay(false);
      }, 3000);
    }
  }, [isLoadingBoard, isLoadingBoardNodes]);

  if (loadingDelay || isLoadingBoard || isLoadingBoardNodes || !boardID) {
    return loadingComponent ? <>{loadingComponent}</> : <BoardLoadingScreen />;
  }

  if (!boardNodes?.length) {
    return <SparkBlankBoard boardID={boardID} />;
  }

  return (
    <Grid
      container
      sx={{ height: '100%', userSelect: 'none', ...boardContainerStyles }}
      onKeyDown={handleKeyEvents}
      tabIndex={0}
      ref={boardRef}
    >
      <Grid
        container
        zIndex='100'
        position={floatingToolBar ? 'fixed' : 'unset'}
        sx={headerStyles}
      >
        <BoardToolBar
          onVIewToggle={handleViewToggle}
          onClose={onClose}
          boardID={boardID}
          isCanvasView={isCanvasView}
          boardElement={boardRef.current}
          setIsAddingNewNode={setIsAddingNewNode}
        />
      </Grid>
      <Box zIndex={2} width='100%' height='100%'>
        <Suspense fallback={<BoardLoadingScreen />}>
          {!isCanvasView ? (
            <Box
              marginTop={8}
              display='flex'
              justifyContent='space-around'
              overflow='auto'
              height='100%'
              paddingBottom='20px'
            >
              <Box
                sx={{
                  maxWidth: '800px',
                  width: '100%',
                  paddingTop: 2,
                }}
              >
                <BoardListViewContainer
                  boardID={boardID}
                  boardNodes={nodes.map((node) => node.data.boardNode)}
                  isParentLoading={isLoadingBoardNodes}
                  isAddingNewNode={isAddingNewNode}
                />
              </Box>
            </Box>
          ) : (
            <BoardCanvasViewContainer boardID={boardID} />
          )}
        </Suspense>
        <Box
          width='100%'
          bottom='10px'
          position='absolute'
          display='flex'
          justifyContent='center'
          zIndex={1000}
          sx={{
            pointerEvents: 'none',
          }}
          className={BOARD_ID_SELECTORS.BOARD_CHAT_CLASS}
        >
          <BoardChatContainer
            chatID={boardChat?.attributes.chatID || ''}
            boardID={boardID}
          />
        </Box>
      </Box>
    </Grid>
  );
}

export default React.memo(BoardContainer);
